import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2,
  MessageCircle,
  Phone,
  CheckCircle2,
  AlertCircle,
  Copy,
  Heart,
  Send,
} from "lucide-react";
import { api } from "@/lib/api";

const FALLBACK_MESSAGE_ML = `നമസ്കാരം 🙏
മരുന്നുകൾ, ആരോഗ്യ രേഖകൾ, ഓർമ്മപ്പെടുത്തലുകൾ, അടിയന്തര SOS അലർട്ടുകൾ, കുടുംബപരിചരണ സഹായം എന്നിവ കാര്യക്ഷമമായി കൈകാര്യം ചെയ്യാൻ കുടുംബങ്ങളെ സഹായിക്കുന്ന Careable 306+ ആപ്പ് നിങ്ങൾ ഉപയോഗിക്കണമെന്ന് ഞാൻ ആഗ്രഹിക്കുന്നു.
ഈ ആപ്പ് ഇൻസ്റ്റാൾ ചെയ്യുന്നതിനും സജ്ജീകരിക്കുന്നതിനും സഹായിക്കുന്നതിനായി Careable-ലെ ശ്രീ. വിവേക് (+91 94227 99196) നിങ്ങളെ വിളിക്കുന്നതാണ്.
താഴെയുള്ള ലിങ്ക് ഉപയോഗിച്ച് ആപ്പ് ഉപയോഗിച്ച് തുടങ്ങാം:
https://careable-plus.emergent.host
നന്ദി 🙏`;

function normalizeDigits(raw) {
  return (raw || "").replace(/\D/g, "");
}

export default function SharePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [messageMl, setMessageMl] = useState(FALLBACK_MESSAGE_ML);
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("91");
  const [submitting, setSubmitting] = useState(false);
  const [shared, setShared] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/beta/share/${token}`);
        if (cancelled) return;
        setName(data?.name || "");
        if (data?.message_ml) setMessageMl(data.message_ml);
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(
          typeof detail === "string"
            ? detail
            : "This share link is invalid or has expired.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submitShare = async (opts = {}) => {
    const digits = opts.skipPhone ? "" : normalizeDigits(phone);
    if (!opts.skipPhone && digits && (digits.length < 7 || digits.length > 14)) {
      setError("Please enter a valid mobile number (7–14 digits, without country code).");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const parent_phone = digits ? `${countryCode}${digits}` : null;
      const { data } = await api.post(`/beta/share/${token}`, { parent_phone });
      setShared(true);
      // Open WhatsApp in a new tab
      if (data?.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Could not record the share right now. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(messageMl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy. Please select and copy manually.");
    }
  };

  if (loading) {
    return (
      <div
        data-testid="share-loading"
        className="min-h-screen bg-[#F4FAF8] flex items-center justify-center px-4"
      >
        <div className="flex items-center gap-2 text-[#1F4E5F]">
          <Loader2 className="w-5 h-5 animate-spin" /> Preparing your share…
        </div>
      </div>
    );
  }

  if (error && !name) {
    return (
      <div
        data-testid="share-error"
        className="min-h-screen bg-[#F4FAF8] flex items-center justify-center px-4"
      >
        <div className="max-w-md w-full bg-white rounded-3xl border border-rose-200 shadow-[0_8px_30px_rgba(220,38,38,0.08)] p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-rose-600" />
          </div>
          <h1 className="font-display text-xl font-bold text-[#0E2A36] mb-2">
            Link unavailable
          </h1>
          <p className="text-sm text-[#3D5560]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4FAF8] to-[#E1EEEA] px-4 py-10 sm:py-16">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="max-w-lg mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold uppercase tracking-wider mb-3">
            <Heart className="w-3.5 h-3.5" /> Share with your parent
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0E2A36]">
            {name ? `${name}, ` : ""}help us bring care to your family
          </h1>
          <p className="mt-2 text-sm text-[#3D5560] max-w-md mx-auto">
            Send this ready-made Malayalam message to your parent or caregiver on
            WhatsApp so a Careable Health Coordinator can help them set up the app.
          </p>
        </div>

        {/* Success state */}
        {shared ? (
          <motion.div
            data-testid="share-success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl border border-emerald-200 shadow-[0_8px_30px_rgba(16,185,129,0.12)] p-8 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 220, damping: 14 }}
              className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-4"
            >
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </motion.div>
            <h2 className="font-display text-xl font-bold text-[#0E2A36] mb-2">
              WhatsApp is opening…
            </h2>
            <p className="text-sm text-[#3D5560] mb-5">
              If it didn&apos;t open automatically, click the button below.
            </p>
            <button
              data-testid="share-open-again"
              type="button"
              onClick={() => submitShare({ skipPhone: !phone })}
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20b858] text-white px-5 py-2.5 rounded-full text-sm font-semibold"
            >
              <MessageCircle className="w-4 h-4" /> Open WhatsApp again
            </button>
            <p className="mt-6 text-xs text-[#3D5560]/80">
              Thank you for helping bring care to more Indian families.
            </p>
          </motion.div>
        ) : (
          <div className="bg-white rounded-3xl border border-[#C9DDD6] shadow-[0_8px_30px_rgba(15,56,32,0.08)] p-6 sm:p-8">
            {/* Message preview */}
            <div className="bg-[#F4FAF8] border border-[#C9DDD6] rounded-2xl p-4 sm:p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#3D5560]/70">
                  Message preview (Malayalam)
                </span>
                <button
                  data-testid="share-copy-message"
                  type="button"
                  onClick={copyMessage}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#1F4E5F] hover:text-[#0E2A36]"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </>
                  )}
                </button>
              </div>
              <p
                data-testid="share-message-preview"
                className="text-[13px] leading-relaxed text-[#0E2A36] whitespace-pre-line"
                style={{ fontFamily: "'Noto Sans Malayalam', system-ui, sans-serif" }}
              >
                {messageMl}
              </p>
            </div>

            {/* Phone input */}
            <div className="mt-6">
              <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
                Parent&apos;s WhatsApp number (optional)
              </label>
              <div className="flex gap-2">
                <div className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#F4FAF8] border border-[#C9DDD6] text-sm text-[#0E2A36]">
                  <Phone className="w-3.5 h-3.5 text-[#3D5560]" />
                  +
                  <input
                    data-testid="share-country-code"
                    type="text"
                    inputMode="numeric"
                    value={countryCode}
                    onChange={(e) =>
                      setCountryCode(normalizeDigits(e.target.value).slice(0, 3))
                    }
                    className="w-10 bg-transparent focus:outline-none text-sm font-semibold"
                  />
                </div>
                <input
                  data-testid="share-phone-input"
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(normalizeDigits(e.target.value).slice(0, 14))}
                  placeholder="9422799196"
                  className="flex-1 min-w-0 px-4 py-2.5 rounded-xl bg-[#F4FAF8] border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm"
                />
              </div>
              <p className="mt-1.5 text-[11px] text-[#3D5560]/80">
                We&apos;ll pre-address the WhatsApp chat with this number. Leave blank
                to pick a contact inside WhatsApp instead.
              </p>
            </div>

            {error && (
              <div
                data-testid="share-inline-error"
                className="mt-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-xs inline-flex items-center gap-2"
              >
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </div>
            )}

            {/* Primary action */}
            <div className="mt-6 space-y-2">
              <button
                data-testid="share-open-whatsapp"
                type="button"
                onClick={() => submitShare()}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20b858] disabled:opacity-60 text-white px-5 py-3 rounded-full text-sm font-bold shadow-[0_6px_20px_rgba(37,211,102,0.25)]"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {phone ? "Send on WhatsApp" : "Open WhatsApp"}
              </button>
              {phone && (
                <button
                  data-testid="share-skip-phone"
                  type="button"
                  onClick={() => submitShare({ skipPhone: true })}
                  disabled={submitting}
                  className="w-full text-xs font-semibold text-[#3D5560] hover:text-[#0E2A36] py-1.5"
                >
                  Skip and pick a contact in WhatsApp instead
                </button>
              )}
            </div>
          </div>
        )}

        {/* Footer note */}
        <p className="mt-6 text-center text-[11px] text-[#3D5560]/70">
          Careable 360+ · Care for your parents, even when you can&apos;t be there.
        </p>
      </motion.div>
    </div>
  );
}
